/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
export type Vertex = number;
export type VertexList = Array<Vertex>;

export enum VColor {
  White = 0,
  Gray = 1,
  Black = 2
}
export type VColorMap = Map<Vertex, VColor>;

export interface IDFSVisitor {
  initializeVertex?: (v: Vertex) => void;
  startVertex?: (v: Vertex) => void;
  discoverVertex?: (v: Vertex) => void;
  examineEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  treeEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  backEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  forwardOrCrossEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  finishEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  finishVertex?: (v: Vertex) => void;
}

export interface IBFSVisitor {
  initializeVertex?: (v: Vertex) => void;
  discoverVertex?: (v: Vertex) => void;
  examineVertex?: (v: Vertex) => void;
  examineEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  treeEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  nonTreeEdge?: (vFrom: Vertex, vTo: Vertex) => void;
  grayTarget?: (vFrom: Vertex, vTo: Vertex) => void;
  blackTarget?: (vFrom: Vertex, vTo: Vertex) => void;
  finishVertex?: (v: Vertex) => void;
}

export enum ESearchDirection {
  Inputs = 'Inputs',
  Outputs = 'Outputs'
}

export type DFVTerminatorFunc = (v: Vertex) => boolean;

interface _VAdjacencyInfo {
  inputs: VertexList;
  outputs: VertexList;
}
type _VAdjacencyMap = Map<Vertex, _VAdjacencyInfo>;
interface _DFVVertexInfo {
  vertex: Vertex;
  srcAdjIdx?: number;
  curAdjIdx: number;
}

export class AdjacencyList {
  private _adjList: _VAdjacencyMap;

  private _nextVertex: number;

  private _verticesPool: Array<Vertex>;

  public constructor() {
    this._adjList = new Map();
    this._nextVertex = 0;
    this._verticesPool = [];
  }

  public addVertex(): Vertex {
    const vertexFromPool = this._verticesPool.shift();
    if (vertexFromPool !== undefined) {
      return this._initVertex(vertexFromPool);
    } else {
      return this._initVertex(this._nextVertex++);
    }
  }

  public addVertexRaw(vertex: Vertex): Vertex {
    if (vertex >= this._nextVertex) {
      this._nextVertex = vertex + 1;
    }
    return this._initVertex(vertex);
  }

  private _initVertex(vertex: Vertex): Vertex {
    if (this._adjList.has(vertex)) {
      throw new EvalError('Already existent');
    }
    this._adjList.set(vertex, { inputs: [], outputs: [] });
    return vertex;
  }

  public addEdge(vFrom: Vertex, vTo: Vertex): void {
    const adjFrom = this._adjList.get(vFrom);
    const adjTo = this._adjList.get(vTo);
    if (adjFrom === undefined || adjTo === undefined) {
      throw new RangeError('Inexistent vertex');
    }
    const adjFromIdx = adjFrom.outputs.indexOf(vTo);
    const adjToIdx = adjTo.inputs.indexOf(vFrom);
    if (adjFromIdx !== -1 || adjToIdx !== -1) {
      throw new EvalError('Already existent');
    }
    adjFrom.outputs.push(vTo);
    adjTo.inputs.push(vFrom);
  }

  public deleteVertex(v: Vertex): void {
    const adj = this._adjList.get(v);
    if (adj === undefined) {
      throw new RangeError('Inexistent vertex');
    }
    if (adj.inputs.length !== 0 || adj.outputs.length !== 0) {
      throw new EvalError('Only unlinked vertices could be deleted. Delete edges first.');
    }
    this._adjList.delete(v);
    this._verticesPool.push(v);
  }

  public deleteEdge(vFrom: Vertex, vTo: Vertex): void {
    const adjFrom = this._adjList.get(vFrom);
    const adjTo = this._adjList.get(vTo);
    if (adjFrom === undefined || adjTo === undefined) {
      throw new RangeError('Inexistent vertex');
    }
    const adjFromIdx = adjFrom.outputs.indexOf(vTo);
    const adjToIdx = adjTo.inputs.indexOf(vFrom);
    if (adjFromIdx === -1 || adjToIdx === -1) {
      throw new EvalError('Inexistent edge');
    }
    adjFrom.outputs.splice(adjFromIdx, 1);
    adjTo.inputs.splice(adjToIdx, 1);
  }

  public getVerticesNumber(): number {
    return this._adjList.size;
  }

  public getVerticesList(): VertexList {
    // A Map object iterates its elements in insertion order:
    //   https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map#Objects_and_maps_compared
    return Array.from(this._adjList.keys());
  }

  public getInputs(v: Vertex): VertexList {
    const adj = this._adjList.get(v);
    if (adj === undefined) {
      throw new RangeError('Inexistent vertex');
    }
    return adj.inputs;
  }

  public getOutputs(v: Vertex): VertexList {
    const adj = this._adjList.get(v);
    if (adj === undefined) {
      throw new RangeError('Inexistent vertex');
    }
    return adj.outputs;
  }

  private static _depthFirstVisitImpl(
    adjacentEdgesAccessor: (v: Vertex) => VertexList,
    startVertex: Vertex,
    vis: IDFSVisitor,
    color: VColorMap,
    func?: DFVTerminatorFunc
  ): void {
    // ported from https://www.boost.org/doc/libs/1_74_0/boost/graph/depth_first_search.hpp
    const stack: Array<_DFVVertexInfo> = [];

    color.set(startVertex, VColor.Gray);
    vis.discoverVertex?.(startVertex);
    stack.push({
      vertex: startVertex,
      srcAdjIdx: undefined,
      curAdjIdx: 0
    });
    // If this vertex terminates the search, we push empty adjacent edges range.
    if (func?.(startVertex)) {
      stack[0].curAdjIdx = adjacentEdgesAccessor(startVertex).length;
    }
    while (stack.length !== 0) {
      const back = stack.pop()!;
      let u = back.vertex;
      let si = back.srcAdjIdx;
      let ci = back.curAdjIdx;
      let adj = adjacentEdgesAccessor(u);
      // finishEdge has to be called here, not after the
      // loop. Think of the pop as the return from a recursive call.
      if (si !== undefined) {
        vis.finishEdge?.(u, adj[si]);
      }
      while (ci < adj.length) {
        const v = adj[ci];
        vis.examineEdge?.(u, v);
        const vColor = color.get(v)!;
        if (vColor === VColor.White) {
          vis.treeEdge?.(u, v);
          si = ci;
          stack.push({
            vertex: u,
            srcAdjIdx: si,
            curAdjIdx: ++ci
          });
          u = v;
          color.set(u, VColor.Gray);
          vis.discoverVertex?.(u);
          ci = 0;
          adj = adjacentEdgesAccessor(u);
          if (func?.(u)) {
            ci = adj.length;
          }
        } else {
          if (vColor === VColor.Gray) {
            vis.backEdge?.(u, adj[ci]);
          } else {
            vis.forwardOrCrossEdge?.(u, adj[ci]);
          }
          vis.finishEdge?.(u, adj[ci]);
          ++ci;
        }
      }
      color.set(u, VColor.Black);
      vis.finishVertex?.(u);
    }
  }

  public depthFirstSearch(
    direction: ESearchDirection,
    vis: IDFSVisitor,
    vertexList?: VertexList,
    startVertex?: Vertex,
    colorMap?: VColorMap,
    func?: DFVTerminatorFunc
  ): void {
    if (direction != ESearchDirection.Inputs && direction != ESearchDirection.Outputs) {
      throw new Error('Logic error');
    }

    const adjacentEdgesAccessor =
      direction == ESearchDirection.Inputs
        ? (v: Vertex): VertexList => this.getInputs(v)
        : (v: Vertex): VertexList => this.getOutputs(v);

    // ported from https://www.boost.org/doc/libs/1_74_0/boost/graph/depth_first_search.hpp
    const color = colorMap || new Map();

    for (const [key] of this._adjList) {
      color.set(key, VColor.White);
      vis.initializeVertex?.(key);
    }

    if (startVertex !== undefined) {
      vis.startVertex?.(startVertex);
      AdjacencyList._depthFirstVisitImpl(adjacentEdgesAccessor, startVertex, vis, color, func);
    }

    if (vertexList === undefined) {
      for (const [key] of this._adjList) {
        if (color.get(key) === VColor.White) {
          vis.startVertex?.(key);
          AdjacencyList._depthFirstVisitImpl(adjacentEdgesAccessor, key, vis, color, func);
        }
      }
    } else {
      for (const vertex of vertexList) {
        if (color.get(vertex) === VColor.White) {
          vis.startVertex?.(vertex);
          AdjacencyList._depthFirstVisitImpl(adjacentEdgesAccessor, vertex, vis, color, func);
        }
      }
    }
  }

  public breadthFirstSearch(
    direction: ESearchDirection,
    vis: IBFSVisitor,
    vertexList: VertexList,
    colorMap?: VColorMap
  ): void {
    if (direction != ESearchDirection.Inputs && direction != ESearchDirection.Outputs) {
      throw new Error('Logic error');
    }

    const adjacentEdgesAccessor =
      direction == ESearchDirection.Inputs
        ? (v: Vertex): VertexList => this.getInputs(v)
        : (v: Vertex): VertexList => this.getOutputs(v);

    // ported from https://www.boost.org/doc/libs/1_74_0/boost/graph/breadth_first_search.hpp
    const color = colorMap || new Map<Vertex, VColor>();

    for (const [key] of this._adjList) {
      color.set(key, VColor.White);
      vis.initializeVertex?.(key);
    }

    const queue = new Array<Vertex>();

    for (const s of vertexList) {
      color.set(s, VColor.Gray);
      vis.discoverVertex?.(s);
      queue.unshift(s);
    }

    while (queue.length !== 0) {
      const u = queue.pop()!;
      vis.examineVertex?.(u);
      for (const v of adjacentEdgesAccessor(u)) {
        vis.examineEdge?.(u, v);
        const vColor = color.get(v);
        if (vColor == VColor.White) {
          vis.treeEdge?.(u, v);
          color.set(v, VColor.Gray);
          vis.discoverVertex?.(v);
          queue.unshift(v);
        } else {
          vis.nonTreeEdge?.(u, v);
          if (vColor == VColor.Gray) {
            vis.grayTarget?.(u, v);
          } else {
            vis.blackTarget?.(u, v);
          }
        }
      }
      color.set(u, VColor.Black);
      vis.finishVertex?.(u);
    }
  }
}
